/**
 * Activité de l'app (mesure maison, API migration 082) et réponses de l'aide
 * (migration 081).
 *
 * GET /api/admin/activity?days=…            vue d'ensemble (administrateurs)
 * GET /api/admin/activity/help-feedback      « Oui / Non » sous les articles d'aide (personnel)
 * GET /api/admin/activity/users/:id          parcours d'un compte (personnel)
 *
 * Jours regroupés à l'heure de Tunis ; chaque total vient avec la période
 * précédente de même durée (`totals_prev`).
 */

export type ActivityDays = 1 | 7 | 30 | 90;
export type Platform = 'android' | 'ios' | 'unknown';

export interface ActivityTotals {
  sessions: number;
  active_users: number;
  anonymous_sessions: number;
  median_session_seconds: number;
  screens_per_session: number;
}

export interface ActivityOverview {
  days: ActivityDays;
  totals: ActivityTotals;
  totals_prev: ActivityTotals;
  daily: { day: string; users: number; sessions: number; android_users: number; ios_users: number }[];
  platforms: { platform: Platform; users: number; sessions: number }[];
  versions: { platform: Platform; app_version: string; users: number; sessions: number }[];
  /** Tous les comptes, d'après leur dernier appel à l'API. */
  devices: { platform: Platform; users: number; seen_in_period: number }[];
  screens: { screen: string; views: number; users: number; avg_duration_ms: number }[];
  help: HelpSummary[];
}

export interface HelpSummary { article: string; yes: number; no: number }

export interface HelpFeedbackItem {
  id_feedback: string;
  article: string;
  helpful: boolean;
  platform: Platform | null;
  app_version: string | null;
  lang: string | null;
  created_at: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  user_photo: string | null;
}

export interface UserJourney {
  user: { id_user: string; name: string | null; email: string | null; last_platform: Platform | null; last_app_version: string | null; last_seen_at: string | null };
  sessions: {
    id_session: string; platform: Platform | null; app_version: string | null; os_version: string | null; lang: string | null;
    started_at: string; last_event_at: string; duration_seconds: number;
    screens: { screen: string; at: string; duration_ms: number | null }[];
  }[];
  help: { article: string; helpful: boolean; platform: Platform | null; app_version: string | null; created_at: string }[];
}

const call = async <T,>(path: string, token: string, signal?: AbortSignal): Promise<T> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` }, signal });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data as T;
};

export const getActivity = (token: string, days: ActivityDays, signal?: AbortSignal) =>
  call<ActivityOverview>(`api/admin/activity?days=${days}`, token, signal);

export const getHelpFeedback = (
  token: string,
  query: { days: ActivityDays; article?: string; helpful?: boolean },
  signal?: AbortSignal
) => {
  const params = new URLSearchParams({ days: String(query.days) });
  if (query.article) params.set('article', query.article);
  if (query.helpful !== undefined) params.set('helpful', String(query.helpful));
  return call<{ days: ActivityDays; items: HelpFeedbackItem[]; summary: HelpSummary[] }>(`api/admin/activity/help-feedback?${params}`, token, signal);
};

export const getUserJourney = (token: string, userId: string, signal?: AbortSignal) =>
  call<UserJourney>(`api/admin/activity/users/${encodeURIComponent(userId)}`, token, signal);

/** Libellés des articles d'aide de l'app (`HelpContentView`, sectionType). */
export const HELP_ARTICLE_LABEL: Record<string, string> = {
  account: 'Mon compte',
  usage: 'Utiliser Dingo',
  ride_problem: 'Un problème de trajet'
};

export const PLATFORM_LABEL: Record<string, string> = { android: 'Android', ios: 'iOS', unknown: 'Inconnu' };

/** Nom lisible d'un écran de l'app (nom court de route, cf. AppActivityHost). */
export const SCREEN_LABEL: Record<string, string> = {
  Loading: 'Démarrage',
  SignIn: 'Connexion',
  Register: 'Inscription',
  SocialProfile: 'Profil (Google / Apple)',
  PasswordReset: 'Mot de passe oublié',
  VerifyEmail: 'Vérification de l’e-mail',
  Map: 'Carte',
  Booking: 'Réservation',
  LicenseVerification: 'Permis de conduire',
  History: 'Mes trajets',
  RideDetail: 'Détail d’un trajet',
  RentalDetails: 'Détail d’une location',
  MangeAllRents: 'Mes locations',
  Rent: 'Partager une mobilité',
  AddVehicleStep1: 'Nouvelle annonce · étape 1',
  AddVehicleStep2: 'Nouvelle annonce · étape 2',
  AddVehicleStep3: 'Nouvelle annonce · étape 3',
  AddVehicleStep4: 'Nouvelle annonce · étape 4',
  EditVehicle: 'Modifier une annonce',
  LocationPicker: 'Choix du lieu',
  VehiclePlanning: 'Planning d’une annonce',
  VehicleAudience: 'Audience d’une annonce',
  Inspection: 'État des lieux',
  OwnerEarnings: 'Mes revenus',
  Favorites: 'Favoris',
  Notifications: 'Notifications',
  Help: 'Aide',
  help_account: 'Aide · Mon compte',
  help_usage: 'Aide · Utiliser Dingo',
  help_ride_problem: 'Aide · Un problème de trajet',
  about_cgu: 'À propos · CGU',
  about_mentions: 'À propos · Mentions légales',
  about_donnees: 'À propos · Données',
  SupportChat: 'Discussion avec le support',
  About: 'À propos',
  Referral: 'Parrainage',
  Settings: 'Paramètres',
  UserProfile: 'Profil',
  EditUser: 'Modifier le profil',
  ChangePassword: 'Changer le mot de passe',
  Payment: 'Paiement',
  PaymentSuccess: 'Paiement confirmé',
  SharedRide: 'Location partagée',
  SharedMobility: 'Annonce partagée',
  TermsAndConditions: 'Conditions d’utilisation',
  PrivacyPolicy: 'Confidentialité'
};
export const screenLabel = (screen: string) => SCREEN_LABEL[screen] || screen;
